import React, { useState, useContext } from "react";
import "./UserInfoDetails.css";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { availableApps } from "../../../utils/helpers";
import { normalizeIntegrationKey } from "../../../utils/integrations";
import { DeleteModal } from "../../modals/Delete";
import { translations } from "../../../utils/lang";
import { Button, Input, Select } from "../../ui";
import StatusDropdown from "../../ui/StatusDropdown";
import { Repeat, Trash2 } from "../../ui/icons";
import SecretCell from "../SecretCell";
import { useStoreCredentials } from "../useStoreCredentials";

const restStatus = [
  "active",
  "inProgress",
  "closed",
  "closedTemp",
  "code",
  "register",
];

export const UserInfoDetails = ({
  userInfo = [],
  appInfo = [],
  onUpdate,
  restaurantId,
}) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const resource = { restaurantId };
  const canManageApps = can("restaurant.app.manage", resource);
  const canUpdateUsers = can("restaurant.userInfo.update", resource);
  const [mode, setMode] = useState("user");
  // Stored passwords are never part of the restaurant data: they are revealed one at a time
  // through the credentials endpoint, and only for roles that may read them.
  const { credentials, loading: secretLoading, failed: secretFailed, reveal, forget } = useStoreCredentials(
    restaurantId,
    canUpdateUsers,
  );
  const [shownSecrets, setShownSecrets] = useState({});

  const [userData, setUserData] = useState(userInfo);
  const [appData, setAppData] = useState(appInfo);
  const [form, setForm] = useState(null); // { app, id, email, pass }
  const [editIndex, setEditIndex] = useState(null); // index or null
  const [, setShowDropdown] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const resetForm = () => {
    setForm(null);
    setEditIndex(null);
    setShowDropdown(false);
  };

  const getAvailableOptions = (currentApp = "") => {
    const selected = (mode === "user" ? userData : appData)
      .map((item, i) => i !== editIndex && (normalizeIntegrationKey(item.app) || item.app))
      .filter(Boolean);
    return availableApps.filter(
      (app) => !selected.includes(app) || app === currentApp,
    );
  };

  const secretKey = (info, index) => String(info._id || `new-${index}`);

  const toggleSecret = async (info, index) => {
    const key = secretKey(info, index);
    if (shownSecrets[key]) {
      setShownSecrets((prev) => ({ ...prev, [key]: false }));
      return;
    }
    if (info._id) {
      const data = await reveal();
      if (!data) return;
    }
    setShownSecrets((prev) => ({ ...prev, [key]: true }));
  };

  const secretValue = (info) => {
    if (info.pass) return info.pass; // just typed, not saved yet
    const stored = (credentials?.userInfo || []).find((entry) => String(entry._id) === String(info._id));
    return stored?.pass || "";
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const confirmDelete = (index) => {
    setDeleteTarget(index);
  };

  const handleConfirmedDelete = (index) => {
    if (!can("restaurant.userInfo.delete", resource)) return;
    let updated;
    if (mode === "user") {
      updated = [...userData];
      updated.splice(index, 1);
      setUserData(updated);
      onUpdate && onUpdate({ userInfo: updated, appInfo });
    } else {
      updated = [...appData];
      updated.splice(index, 1);
      setAppData(updated);
      onUpdate && onUpdate({ userInfo, appInfo: updated });
    }
    resetForm();
    setDeleteTarget(null);
  };

  const handleSave = () => {
    if (!can(mode === "user" ? "restaurant.userInfo.update" : "restaurant.app.manage", resource)) return;
    if (mode === "user") {
      // A password is required for a NEW account, or when none is stored yet. Editing an
      // existing account without typing one keeps the stored password (the server preserves it).
      const hasStoredPassword = editIndex !== null && Boolean(form.passwordAvailable);
      if (!form.app || !form.id || !form.email) return;
      if (!form.pass && !hasStoredPassword) return;
      const { passwordAvailable, ...entry } = form;
      if (!entry.pass) delete entry.pass;
      const updated = [...userData];
      if (editIndex !== null) updated[editIndex] = { ...entry, ...(passwordAvailable && !entry.pass ? { passwordAvailable } : {}) };
      else updated.push(entry);
      setUserData(updated);
      onUpdate && onUpdate({ userInfo: updated, appInfo });
      forget();
      setShownSecrets({});
    } else {
      if (!form.app || !form.name || !form.email || !form.phone) return;
      const updated = [...appData];
      if (editIndex !== null) updated[editIndex] = form;
      else updated.push(form);
      setAppData(updated);
      onUpdate && onUpdate({ userInfo, appInfo: updated });
    }
    resetForm();
  };

  const handleUpdateClick = (index) => {
    setEditIndex(index);
    setForm(mode === "user" ? { ...userData[index], pass: "" } : appData[index]);
    setShowDropdown(false);
  };

  const handleAddClick = () => {
    if (mode === "user") {
      setForm({ app: "", id: "", email: "", pass: "", status: "active" });
    } else {
      setForm({ app: "", name: "", email: "", phone: "" });
    }
    setEditIndex(null);
    setShowDropdown(false);
  };

  const handleStatusChange = (status, index) => {
    if (!can("restaurant.userInfo.update", resource)) return;
    let updated;

    if (mode === "user") {
      updated = [...userData];
      updated[index] = { ...updated[index], status };
      setUserData(updated);
      onUpdate && onUpdate({ userInfo: updated, appInfo });
    } else {
      updated = [...appData];
      updated[index] = { ...updated[index], status };
      setAppData(updated);
      onUpdate && onUpdate({ userInfo, appInfo: updated });
    }
  };

  const currentData = mode === "user" ? userData : appData;

  return (
    <div className="userInfo-container">
      <div className="userInfo-top">
        <h3>
          {
            translations[lang].restaurants.restaurantsRow.userInfo.title[
              mode === "user" ? 0 : 1
            ]
          }
        </h3>
        {canManageApps && (
          <Button
            type="button"
            variant="primary"
            appearance="soft"
            onClick={() => {
              resetForm();
              setMode(mode === "user" ? "app" : "user");
            }}
          >
            {mode === "user"
              ? translations[lang].restaurants.restaurantsRow.userInfo.title[1]
              : translations[lang].restaurants.restaurantsRow.userInfo.title[0]}
          </Button>
        )}
      </div>
      <div className="restaurant-subtable-scroll">
        <table className="userInfo-table-container">
          <thead>
            <tr>
              {translations[
                lang
              ].restaurants.restaurantsRow.userInfo.userInfoHeaders[
                mode === "user" ? 0 : 1
              ].map((item, i) =>
                translations[lang].restaurants.restaurantsRow.userInfo
                  .userInfoHeaders[mode === "user" ? 0 : 1].length ===
                  i + 1 &&
                !canUpdateUsers ? null : (
                  <th className="userInfo-table-header" key={i}>
                    {item}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {currentData.length === 0 && (
              <tr>
                <td colSpan="5">
                  {translations[lang].restaurants.restaurantsRow.userInfo.noUser}
                </td>
              </tr>
            )}
            {currentData.map((info, i) => (
              <tr key={i}>
                <td className="userInfo-table-p">
                  {translations[lang].appsNames[info.app] ||
                    translations[lang].notApp + " " + (i + 1)}
                </td>
                {mode === "user" ? (
                  <>
                    <td>{info.id}</td>
                    <td>{info.email}</td>
                    <td>
                      <SecretCell
                        available={Boolean(info.pass || info.passwordAvailable)}
                        value={secretValue(info)}
                        shown={Boolean(shownSecrets[secretKey(info, i)])}
                        loading={secretLoading}
                        failed={secretFailed}
                        canReveal={canUpdateUsers && Boolean(info._id || info.pass)}
                        onToggle={() => toggleSecret(info, i)}
                        lang={lang}
                      />
                    </td>
                    <td className="restaurants-list-table-p">
                      <StatusDropdown
                        value={info.status || "active"}
                        options={restStatus}
                        editable={canUpdateUsers}
                        onChange={(status) => handleStatusChange(status, i)}
                        getLabel={(status) =>
                          translations[lang].restaurants.restaurantsRow.status[
                            status
                          ]
                        }
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td>{info.name}</td>
                    <td>{info.email}</td>
                    <td>{info.phone}</td>
                  </>
                )}
                {canUpdateUsers && (
                  <td className="userInfo-table-btns">
                    <Button
                      type="button"
                      variant="primary"
                      appearance="soft"
                      icon={<Repeat size={16} />}
                      onClick={() => handleUpdateClick(i)}
                    />
                    {can("restaurant.userInfo.delete", resource) && (
                      <Button
                        type="button"
                        variant="danger"
                        appearance=""
                        icon={<Trash2 size={18} />}
                        onClick={() => confirmDelete(i)}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form ? (
        <div className="userInfo-form">
          <Select
            placeholder={translations[lang].restaurants.select}
            value={form.app || ""}
            onChange={(e) => handleChange("app", e.target.value)}
            options={getAvailableOptions(form.app).map((app) => ({
              value: app,
              label: translations[lang].appsNames[app] || app,
            }))}
          />
          {mode === "user" ? (
            <div className="userInfo-form-grid">
              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[0]
                  }
                </p>
                <Input
                  type="text"
                  value={form.id}
                  placeholder="id"
                  onChange={(e) => handleChange("id", e.target.value)}
                />
              </div>

              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[1]
                  }
                </p>
                <Input
                  type="email"
                  placeholder="email@email.com"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>

              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[2]
                  }
                </p>
                <Input
                  type="password"
                  placeholder={
                    form.passwordAvailable
                      ? lang === "ar" ? "اتركه فارغًا للإبقاء على الحالية" : "Leave blank to keep the current one"
                      : "password"
                  }
                  autoComplete="new-password"
                  value={form.pass || ""}
                  onChange={(e) => handleChange("pass", e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="userInfo-form-grid">
              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[3]
                  }
                </p>
                <Input
                  type="text"
                  value={form.name}
                  placeholder="name"
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>

              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[1]
                  }
                </p>
                <Input
                  type="email"
                  placeholder="email@email.com"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>

              <div className="form-row">
                <p>
                  {
                    translations[lang].restaurants.restaurantsRow.userInfo
                      .inputHeader[4]
                  }
                </p>
                <Input
                  type="phone"
                  placeholder="phone"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="userInfo-form-actions">
            <Button
              type="button"
              variant="primary"
              appearance="filled"
              fullWidth
              onClick={handleSave}
            >
              {editIndex !== null
                ? translations[lang].restaurants.restaurantsRow.userInfo.save
                : translations[lang].restaurants.restaurantsRow.userInfo.add}
            </Button>
            <Button
              type="button"
              variant="danger"
              appearance="soft"
              fullWidth
              onClick={resetForm}
            >
              {translations[lang].restaurants.restaurantsRow.userInfo.cancel}
            </Button>
          </div>
        </div>
      ) : (
        canManageApps &&
        currentData.length < availableApps.length && (
          <Button
            type="button"
            variant="primary"
            appearance="soft"
            onClick={handleAddClick}
          >
            {translations[lang].restaurants.restaurantsRow.userInfo.add}
          </Button>
        )
      )}
      {deleteTarget !== null && (
        <DeleteModal
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
          cancel={setDeleteTarget}
        />
      )}
    </div>
  );
};